import React, { useState, useEffect, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { clearErrors, getProducts } from "../../actions/productAction";
import Loader from "../Layouts/Loader";
import MetaData from "../Layouts/MetaData";
import Product from "./Product";
import Pagination from "@mui/material/Pagination";
import Slider from "@mui/material/Slider";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import StarIcon from "@mui/icons-material/Star";

// Filter Components
const PriceFilter = memo(({ price, onPriceChange }) => (
  <div className="flex flex-col gap-3 border-b py-4">
    <span className="font-medium text-xs uppercase">Price Range</span>
    <Slider
      value={price}
      onChange={onPriceChange}
      valueLabelDisplay="auto"
      getAriaLabel={() => "Price range slider"}
      min={0}
      max={5000}
    />
    <div className="flex gap-4 items-center justify-between mt-2">
      <span className="px-4 py-1 rounded-sm text-gray-800 bg-gray-100">
        ₹{price[0].toLocaleString()}
      </span>
      <span className="font-medium text-gray-500">to</span>
      <span className="px-4 py-1 rounded-sm text-gray-800 bg-gray-100">
        ₹{price[1].toLocaleString()}
      </span>
    </div>
  </div>
));

const RatingsFilter = memo(({ ratings, setRatings, toggle, setToggle }) => (
  <div className="flex flex-col py-4">
    <div
      className="flex justify-between cursor-pointer"
      onClick={() => setToggle(!toggle)}
    >
      <p className="font-medium text-xs uppercase">Ratings</p>
      {toggle ? (
        <ExpandLessIcon sx={{ fontSize: "20px" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: "20px" }} />
      )}
    </div>
    {toggle && (
      <div className="flex flex-col mt-2">
        <FormControl>
          <RadioGroup
            aria-labelledby="ratings-radio-buttons-group"
            onChange={(e) => setRatings(e.target.value)}
            value={ratings}
            name="ratings-radio-buttons"
          >
            {[4, 3, 2, 1].map((el, i) => (
              <FormControlLabel
                key={i}
                value={el}
                control={<Radio size="small" />}
                label={
                  <span className="flex items-center text-sm">
                    {el}
                    <StarIcon sx={{ fontSize: "12px", mr: 0.5 }} /> & above
                  </span>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
      </div>
    )}
  </div>
));

const ProductList = ({ products }) => (
  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 p-2">
    {products?.map((product) => (
      <div key={product._id} className="px-0">
        <Product {...product} />
      </div>
    ))}
  </div>
);

const Products = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const location = useLocation();

  const [price, setPrice] = useState([0, 5000]);
  const [category, setCategory] = useState(
    location.search ? location.search.split("=")[1] : ""
  );
  const [ratings, setRatings] = useState(0);
  const [ratingsToggle, setRatingsToggle] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const { products, loading, error, resultPerPage, filteredProductsCount } =
    useSelector((state) => state.products);
  const keyword = params.keyword;

  const priceHandler = (e, newPrice) => {
    setPrice(newPrice);
  };

  const clearFilters = () => {
    setPrice([0, 5000]);
    setCategory("");
    setRatings(0);
  };

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      dispatch(clearErrors());
    }
    dispatch(getProducts(keyword, category, price, ratings, currentPage));
  }, [
    dispatch,
    keyword,
    category,
    price,
    ratings,
    currentPage,
    error,
    enqueueSnackbar,
  ]);

  return (
    <>
      <MetaData
        title="All Products | Siddhi Creatives"
        description="Explore the full range of creative products at Siddhi Creatives. Browse our collection of high-quality creative tools, services, and products designed to enhance your creative journey."
        keywords="all products, creative products, buy creative tools, Siddhi Creatives, product collection, creative services, browse products"
        ogTitle="All Products | Siddhi Creatives"
        ogDescription="Discover a wide selection of creative products at Siddhi Creatives. From creative tools to services, explore products that elevate your creative experience."
        ogUrl="https://www.siddhicreatives.com/products"
      />

      <main className="w-full mt-20 sm:mt-20 bg-gray-50">
        <div className="flex flex-wrap  mt-2 sm:mt-3 mx-auto px-4 sm:px-6 lg:px-8 max-w-full">
          {/* Sidebar (Filter) */}
          <div className="w-full sm:w-1/5 lg:w-1/6 px-2">
            <div className="bg-white rounded-lg shadow-md">
              <div className="flex items-center justify-between px-2  py-2 border-b border-gray-200">
                <p className="text-lg font-semibold text-gray-700">Filters</p>
                <span
                  className="text-primary-blue text-xs cursor-pointer font-medium hover:text-blue-600"
                  onClick={clearFilters}
                >
                  Clear All
                </span>
              </div>
              <div className="px-4 py-3 text-sm text-gray-600">
                <PriceFilter price={price} onPriceChange={priceHandler} />
                <RatingsFilter
                  ratings={ratings}
                  setRatings={setRatings}
                  toggle={ratingsToggle}
                  setToggle={setRatingsToggle}
                />
              </div>
            </div>
          </div>

          {/* Product Display */}
          <div className="w-full sm:w-4/5 lg:w-5/6">
            {!loading && products?.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-4 bg-white shadow-sm rounded-md p-8 sm:p-16">
                <img
                  draggable="false"
                  className="w-1/2 h-44 object-contain"
                  src="https://static-assets-web.flixcart.com/www/linchpin/fk-cp-zion/img/error-no-search-results_2353c5.png"
                  alt="Search Not Found"
                />
                <h1 className="text-2xl font-medium text-gray-800">
                  Sorry, no results found!
                </h1>
                <p className="text-xl text-center text-primary-grey">
                  Please check the spelling or try searching for something else.
                </p>
              </div>
            )}

            {loading ? <Loader /> : <ProductList products={products} />}

            {/* Pagination */}
            {filteredProductsCount > resultPerPage && (
              <div className="flex justify-center pt-6">
                <Pagination
                  count={Math.ceil(filteredProductsCount / resultPerPage)}
                  page={currentPage}
                  onChange={(e, val) => setCurrentPage(val)}
                  color="primary"
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default Products;
